import clsx from 'clsx';
import { useAtom } from 'jotai';
import { memo, useEffect, useId, useRef, useState } from 'react';
import type { Editor as TiptapEditor, EditorOptions } from '@tiptap/react';
import { EditorContent, useEditorState as useTiptapEditorState } from '@tiptap/react';
import type { Dispatch, SetStateAction, CSSProperties, ReactNode } from 'react';

import { useApiRequestState } from '@/hooks/api';
import { useAgentSessionState } from '@/hooks/llm';
import { useBlockEditor } from '@/hooks/use-block-editor';
import {
  resumeAgentRunAutoScroll,
  useEditorState,
  getAgentRunStreamingEnabled,
  clearHydratedReview,
  isReviewHydrated,
  restorePendingReview,
  type PendingReview,
  type PersistedDocumentWriter,
} from '@/hooks/llm/use-llm-agent';
import { useElementResize } from '@flintmint/web-shared/hooks/use-element-resize';
import {
  setEditorComponentMounted,
  setEditorDocumentId,
  setEditorPersistDocumentPatch,
  setEditorType,
} from '@/hooks/llm/editor-suggestion-config';

import { Toolbar } from '@/components/toolbar';
import { ToCRail } from '@/components/toc-rail';
import { LlmLoading } from '@/components/llm-loading';
import { Collapse } from '@flintmint/web-shared/components/collapse';

import { useStreamingEnabled } from '@/hooks/use-streaming-enabled';

import { toRem } from '@flintmint/web-shared/utils/ui';
import { isEqualHTML, focusEditorStart, getEditorHTML, isEditorAlive } from '@/utils/editor-utils';

import { getTextGenerationKey, textGenerationMapAtom } from '@/state';

import styles from './editor.module.scss';

export const Editor = memo((props: EditorProps) => {
  const {
    canSync,
    cacheable,
    options,
    onUpdate,
    setEditor,
    autofocus,
    className,
    processing,
    editorType,
    documentId,
    promptBox,
    placeholder,
    llmRequestId,
    persistDocumentPatch,
    content,
    didInitNormalize,
    setDidInitNormalize,
    scrollParentId,
    selfScrollable,
    processingView,
    editable = true,
    hasToolbar = true,
    pendingReview,
    onContentSynced,
    editorContentClassName,
    editorContentTestId,
    ...rest
  } = props;

  const [textGenerationMap] = useAtom(textGenerationMapAtom);
  const selectionBoxId = useId();
  const editorContainerId = useId();

  const { ref: containerRef, clientWidth } = useElementResize();

  const scrollerId = selfScrollable ? editorContainerId : scrollParentId;

  const streamingEnabled = useStreamingEnabled();
  const { isLoading: isLLMRequestLoading } = useApiRequestState(documentId ?? editorType);
  const { isLoading: isAgentLoading } = useAgentSessionState(llmRequestId);
  const editorState = useEditorState(llmRequestId);
  const isAgentWriting = editorState === 'agent_writing';
  const isEditorInteractive = editorState === 'interactive';
  const isReviewing = !!pendingReview;
  const isLLMLoading = isLLMRequestLoading || isAgentWriting;
  const isLockedByLLM =
    !!llmRequestId && (isAgentWriting ? !getAgentRunStreamingEnabled(llmRequestId) : !streamingEnabled && isLLMRequestLoading);

  const textGenerationKey = getTextGenerationKey({ documentId, editorType });
  const restoredFocusPos = textGenerationMap[textGenerationKey]?.focusPos;

  const { editor } = useBlockEditor({
    content,
    cacheable,
    onUpdate,
    editable,
    editorType,
    documentId,
    placeholder,
    llmRequestId,
    selectionBoxId,
    editorContainerId,
    scrollParentId: scrollerId,
    autofocus: restoredFocusPos || autofocus,
    ...options,
  });
  const isEditorFocused = useTiptapEditorState({
    editor,
    selector: ({ editor }) => editor.isFocused,
  });
  const isWindowFocused = useWindowFocused();

  useEffect(() => {
    editor?.on('update', onUpdate);
    return () => {
      editor?.off('update', onUpdate);
    };
  }, [editor, onUpdate]);

  const restoredFocusPosRef = useRef(restoredFocusPos);
  useEffect(() => {
    restoredFocusPosRef.current = restoredFocusPos;
  }, [restoredFocusPos]);

  // Autofocus only on editor creation: a Generate Text position set by then is restored below instead
  useEffect(() => {
    if (!editor || !autofocus || restoredFocusPosRef.current) return;
    queueMicrotask(() => {
      try {
        focusEditorStart(editor);
      } catch {
        // Editor view may not be mounted yet
      }
    });
  }, [editor, autofocus]);

  // Only a set position moves the selection: clearing it must leave the slash menu and its typed prompt open
  useEffect(() => {
    if (!editor || !restoredFocusPos) return;
    queueMicrotask(() => {
      try {
        editor.commands.focus(restoredFocusPos);
      } catch {
        // Editor view may not be mounted yet
      }
    });
  }, [editor, restoredFocusPos]);

  useEffect(() => {
    if (editor && llmRequestId) {
      resumeAgentRunAutoScroll(llmRequestId);
    }
  }, [editor, documentId, llmRequestId]);

  useEffect(() => {
    editor?.setOptions({ editable: editable && isEditorInteractive && !isReviewing });
  }, [editor, editable, isEditorInteractive, isReviewing]);

  useEffect(() => {
    const editorReady = editor && isEditorAlive(editor) && isEditorInteractive;
    const shouldSkipSync =
      processing ||
      !editorReady ||
      canSync === false ||
      !!pendingReview ||
      (isEditorFocused && isWindowFocused) ||
      isEqualHTML(content, getEditorHTML(editor), editor);

    if (shouldSkipSync) return;

    editor.commands.setContent(content, { emitUpdate: false });
    onContentSynced?.(getEditorHTML(editor));
  }, [
    canSync,
    processing,
    content,
    editor,
    isEditorFocused,
    isWindowFocused,
    isEditorInteractive,
    pendingReview,
    onContentSynced,
  ]);

  useEffect(() => {
    if (editor) {
      setEditorType(editor, editorType);
    }
  }, [editor, editorType]);

  useEffect(() => {
    if (editor && documentId) {
      setEditorDocumentId(editor, documentId);
    }
  }, [editor, documentId]);

  useEffect(() => {
    if (editor) {
      setEditorPersistDocumentPatch(editor, persistDocumentPatch);
    }
  }, [editor, persistDocumentPatch]);

  useEffect(() => {
    if (editor) {
      let cleanupReviewRestore: (() => void) | undefined;
      setEditorComponentMounted(editor, true);
      cleanupReviewRestore = restoreReviewState({
        editor,
        pendingReview,
      });

      return () => {
        cleanupReviewRestore?.();
        setEditorComponentMounted(editor, false);
      };
    }
  }, [editor, documentId, llmRequestId, pendingReview]);

  useEffect(() => {
    editor && setEditor?.(editor);
  }, [editor]);

  /**
   * Tracks when Tiptap/ProseMirror has completed its initial HTML normalization.
   * Prevents autosave from triggering on normalization changes (whitespace cleanup, empty paragraph removal)
   * by waiting for the first update event before enabling autosave in the parent component.
   * Reasoning https://github.com/flintmint/documint/pull/440
   */
  useEffect(() => {
    if (!didInitNormalize && !!setDidInitNormalize) {
      editor?.on('update', () => {
        if (!didInitNormalize) {
          setDidInitNormalize(true);
        }
      });

      editor.on('create', (ctx) => {
        if (!didInitNormalize && isEqualHTML(content, getEditorHTML(ctx.editor), ctx.editor)) {
          setDidInitNormalize(true);
        }
      });
    }
  }, [editor, setDidInitNormalize]);

  useEffect(() => {
    return () => {
      setEditor?.(null);
    };
  }, []);

  return (
    <>
      <div
        ref={containerRef}
        id={editorContainerId}
        className={clsx(styles.editor, className, {
          [styles.readonly]: !editable || editorState === 'review' || isReviewing,
          [styles.processing]: processing,
          [styles.locked]: isAgentWriting || isLLMRequestLoading || restoredFocusPos,
          [styles.selfScrollable]: selfScrollable,
        })}
        data-editortype={editorType}
        data-streaming={isAgentLoading}
        style={
          {
            ['--editor-width']: toRem(clientWidth),
          } as CSSProperties
        }
      >
        {hasToolbar && (
          <div className={styles.toolbarContainer}>
            {props.hasOwnProperty('editable') ? (
              <Collapse open={editable}>
                <Toolbar editor={editor!} processing={processing} streaming={isLLMLoading} />
              </Collapse>
            ) : (
              <Toolbar editor={editor!} processing={processing} streaming={isLLMLoading} />
            )}
          </div>
        )}
        {processing ? (
          <div className={styles.processingSlot}>{processingView}</div>
        ) : (
          <EditorContent
            editor={editor}
            className={clsx(styles.editorContent, editorContentClassName)}
            data-testid={editorContentTestId}
            {...rest}
          />
        )}
        <ToCRail editor={editor} />
      </div>
      {!processing && !!llmRequestId && (
        <div className={styles.llmLoadingHolder}>
          <LlmLoading active={isLockedByLLM} />
        </div>
      )}
      {promptBox?.({ style: { maxWidth: toRem(clientWidth) } })}
    </>
  );
});

export default Editor;

function useWindowFocused(): boolean {
  const [isWindowFocused, setIsWindowFocused] = useState(() => document.hasFocus());

  useEffect(() => {
    const handleFocus = () => setIsWindowFocused(true);
    const handleBlur = () => setIsWindowFocused(false);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  return isWindowFocused;
}

function restoreReviewState({
  editor,
  pendingReview,
}: {
  editor: TiptapEditor;
  pendingReview?: PendingReview | null;
}): (() => void) | undefined {
  if (!pendingReview) {
    clearHydratedReview(editor);
    return undefined;
  }
  if (isReviewHydrated(editor, pendingReview)) return undefined;
  return restorePendingReview(editor, pendingReview);
}

export interface EditorProps {
  canSync?: boolean;
  content: string;
  editorType: string;
  className?: string;
  editable?: boolean;
  documentId?: string;
  llmRequestId?: string;
  pendingReview?: PendingReview | null;
  persistDocumentPatch?: PersistedDocumentWriter;
  autofocus?: boolean;
  processing?: boolean;
  placeholder?: string;
  hasToolbar?: boolean;
  promptBox?: (props: { style: CSSProperties }) => ReactNode;
  scrollParentId?: string;
  selfScrollable?: boolean;
  processingView?: ReactNode;
  editorContentClassName?: string;
  editorContentTestId?: string;
  options?: Partial<Omit<EditorOptions, 'content'>>;
  onUpdate: EditorOptions['onUpdate'];
  cacheable?: boolean;
  didInitNormalize?: boolean; // Used to prevent autosave from triggering on normalization changes when a document is first loaded
  setDidInitNormalize?: Dispatch<SetStateAction<boolean>>; // flag to true signals that normalization is complete and subsequent updates are actual user changes
  setEditor?: (editor: TiptapEditor | null) => void;
  onContentSynced?: (html: string) => void;
}
